/**
 * Canonical dependency-free utility helpers used across client, server, and Convex.
 */

import type { DeployActivityLike, DeployAppLike } from "./types";

function fileExtension(path: string): string {
	const base = basenameFromPath(path);
	const index = base.lastIndexOf(".");
	return index > 0 ? base.slice(index) : "";
}

function resolveProjectRoot(): string {
	if (typeof process === "undefined") return ".";
	const envRoot = process.env?.POINT_PROJECT_ROOT?.trim();
	if (envRoot) return envRoot;
	if (typeof process.cwd !== "function") return ".";
	const cwd = process.cwd();
	if (cwd.endsWith("/scripts")) return cwd.slice(0, -"/scripts".length);
	if (cwd.endsWith("/bun")) return cwd.slice(0, -"/bun".length);
	return cwd;
}

export const PROJECT_ROOT = resolveProjectRoot();

export function formatDateKey(date: Date = new Date()): string {
	const year = date.getFullYear();
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${year}-${month}-${day}`;
}

export function isoDate(offsetDays = 0): string {
	const date = new Date();
	if (offsetDays !== 0) date.setDate(date.getDate() + offsetDays);
	return date.toISOString().split("T")[0] ?? "";
}

export function dayKey(ts: number): string {
	return formatDateKey(new Date(ts));
}

export function lastNDays(count: number): string[] {
	return Array.from({ length: count }, (_, i) => {
		const d = new Date();
		d.setDate(d.getDate() - (count - 1 - i));
		return formatDateKey(d);
	});
}

export function timeAgo(ts: number | string): string {
	const time = typeof ts === "string" ? new Date(ts).getTime() : ts;
	const diff = Date.now() - time;
	const mins = Math.floor(diff / 60_000);
	if (mins < 1) return "now";
	if (mins < 60) return `${mins}m`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h`;
	return `${Math.floor(hours / 24)}d`;
}

export function formatDuration(ms: number): string {
	const totalSeconds = Math.max(0, Math.floor(ms / 1000));
	if (totalSeconds < 60) return `${totalSeconds}s`;
	const minutes = Math.floor(totalSeconds / 60);
	const seconds = totalSeconds % 60;
	if (minutes < 60) return `${minutes}m ${seconds}s`;
	const hours = Math.floor(minutes / 60);
	return `${hours}h ${minutes % 60}m`;
}

export function formatRelativeTime(ts?: number | null, fallback: string = "—"): string {
	if (!ts) return fallback;
	const diff = Date.now() - ts;
	const mins = Math.floor(diff / 60_000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 30) return `${days}d ago`;
	return new Date(ts).toLocaleDateString();
}

export function formatDateTime(ts?: number | null, fallback: string = "Not scheduled"): string {
	if (!ts) return fallback;
	return new Date(ts).toLocaleString([], {
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

function deploymentMilestoneTimestamp(app: DeployAppLike): number {
	const deployment = app.deployment;
	const timestamp = Math.max(
		typeof deployment?.latestBuildUploadedAt === "number" ? deployment.latestBuildUploadedAt : 0,
		typeof deployment?.submittedAt === "number" ? deployment.submittedAt : 0,
		typeof deployment?.latestVersionCreatedAt === "number" ? deployment.latestVersionCreatedAt : 0,
		typeof app.firstLiveAt === "number" ? app.firstLiveAt : 0,
		typeof app.deployedAt === "number" ? app.deployedAt : 0,
	);
	if (timestamp <= 0) return 0;
	if (deployment?.buildUploaded === true || deployment?.submittedForReview === true || deployment?.isLive === true) {
		return timestamp;
	}
	return 0;
}

export function countQualifiedDeployApps(activities: DeployActivityLike[], appsById: Map<string, DeployAppLike>, weekStart: number, weekEnd: number): number {
	const appIds = new Set<string>();
	for (const activity of activities) {
		if (activity.action !== "deploy" || !activity.appId) continue;
		const app = appsById.get(String(activity.appId));
		if (!app) continue;
		const milestoneAt = deploymentMilestoneTimestamp(app);
		if (milestoneAt >= weekStart && milestoneAt < weekEnd) {
			appIds.add(String(activity.appId));
		}
	}
	return appIds.size;
}

export function formatDatetimeLocalValue(value?: number | string): string {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}T${`${date.getHours()}`.padStart(2, "0")}:${`${date.getMinutes()}`.padStart(2, "0")}`;
}

export function clamp(value: number, min: number, max: number) {
	return Math.max(min, Math.min(max, value));
}

export function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

export function renderPromptTemplate(template: string, replacements: Record<string, string>): string {
	return Object.entries(replacements).reduce((result, [token, value]) => result.replaceAll(`{${token}}`, value), template);
}

export function renderPromptLines(lines: readonly string[], replacements: Record<string, string> = {}): string[] {
	return lines.map((line) => renderPromptTemplate(line, replacements));
}

export function renderPromptBlock(lines: readonly string[], replacements: Record<string, string> = {}): string {
	return renderPromptLines(lines, replacements).join("\n");
}

export function renderPromptValue(value: unknown, replacements: Record<string, string> = {}): string {
	if (Array.isArray(value))
		return renderPromptBlock(
			value.map((line) => String(line)),
			replacements,
		);
	if (typeof value === "string") return renderPromptTemplate(value, replacements);
	return "";
}

export function renderBulletList(items: readonly string[], replacements: Record<string, string> = {}): string {
	return renderBulletLines(items, replacements).join("\n");
}

export function renderBulletLines(items: readonly string[], replacements: Record<string, string> = {}): string[] {
	return renderPromptLines(items, replacements).map((item) => `- ${item}`);
}

export function renderBulletListOr(items: readonly string[] | undefined | null, fallback: string, replacements: Record<string, string> = {}): string {
	return items?.length ? renderBulletList(items, replacements) : fallback;
}

export function optionalEnv(name: string, env: Record<string, string | undefined> = process.env): string | null {
	const value = env[name];
	return jsonString(value);
}

export type RequiredEnvResult<T extends string> = { ok: true; values: Record<T, string> } | { ok: false; missing: T[] };

export function requiredEnv<T extends string>(names: readonly T[], env?: Record<string, string | undefined>): RequiredEnvResult<T> {
	const values = {} as Record<T, string>;
	const missing: T[] = [];
	for (const name of names) {
		const value = optionalEnv(name, env);
		if (value) values[name] = value;
		else missing.push(name);
	}
	return missing.length > 0 ? { ok: false, missing } : { ok: true, values };
}

export function normalizeEscapedNewlines(value: string): string {
	return value.replace(/\\n/g, "\n");
}

function browserLocalStorage(): Storage | null {
	return typeof localStorage === "undefined" ? null : localStorage;
}

/**
 * Point replaced the legacy Surgent product namespace. Keep browser state
 * readable and writable by both released generations while upgrades are in
 * the field. Point is canonical when both values exist.
 */
function alternateBrandStorageKey(key: string): string | null {
	if (key.includes("point")) return key.replaceAll("point", "surgent");
	if (key.includes("Point")) return key.replaceAll("Point", "Surgent");
	if (key.includes("POINT")) return key.replaceAll("POINT", "SURGENT");
	if (key.includes("surgent")) return key.replaceAll("surgent", "point");
	if (key.includes("Surgent")) return key.replaceAll("Surgent", "Point");
	if (key.includes("SURGENT")) return key.replaceAll("SURGENT", "POINT");
	return null;
}

function readCompatibleStorageValue(storage: Storage, key: string): string | null {
	const canonical = storage.getItem(key);
	if (canonical !== null) return canonical;
	const alternateKey = alternateBrandStorageKey(key);
	if (!alternateKey) return null;
	const legacy = storage.getItem(alternateKey);
	if (legacy !== null) storage.setItem(key, legacy);
	return legacy;
}

function writeCompatibleStorageValue(storage: Storage, key: string, value: string | null): void {
	const alternateKey = alternateBrandStorageKey(key);
	for (const target of alternateKey ? [key, alternateKey] : [key]) {
		if (value === null) storage.removeItem(target);
		else storage.setItem(target, value);
	}
}

export function readStoredJson<T>(key: string, fallback: T): T {
	try {
		const storage = browserLocalStorage();
		if (!storage) return fallback;
		const stored = readCompatibleStorageValue(storage, key);
		return stored ? (JSON.parse(stored) as T) : fallback;
	} catch {
		return fallback;
	}
}

export function writeStoredJson<T>(key: string, value: T) {
	try {
		const storage = browserLocalStorage();
		if (storage) writeCompatibleStorageValue(storage, key, JSON.stringify(value));
	} catch {}
}

export function updateStoredJson<T extends object>(key: string, fallback: T, updater: (current: T) => T): T {
	const next = updater(readStoredJson(key, fallback));
	writeStoredJson(key, next);
	return next;
}

export function readStoredValue(key: string, fallback: string | null = null): string | null {
	try {
		const storage = browserLocalStorage();
		if (!storage) return fallback;
		const stored = readCompatibleStorageValue(storage, key);
		return stored ?? fallback;
	} catch {
		return fallback;
	}
}

export function writeStoredValue(key: string, value: string | null): void {
	try {
		const storage = browserLocalStorage();
		if (!storage) return;
		writeCompatibleStorageValue(storage, key, value);
	} catch {}
}

type BunFileReader = {
	exists(): Promise<boolean>;
	text(): Promise<string>;
};

function bunFile(path: string): BunFileReader | null {
	const runtime = globalThis as typeof globalThis & { Bun?: { file(path: string): BunFileReader } };
	return runtime.Bun?.file(path) ?? null;
}

export async function readJsonFile<T>(path: string, fallback: T): Promise<T> {
	try {
		const file = bunFile(path);
		if (!file || !(await file.exists())) return fallback;
		return JSON.parse(await file.text()) as T;
	} catch {
		return fallback;
	}
}

export async function fileExists(path: string): Promise<boolean> {
	try {
		return (await bunFile(path)?.exists()) ?? false;
	} catch {
		return false;
	}
}

export function isGptImageModel(model: string): boolean {
	const normalized = model.toLowerCase();
	return normalized.includes("gpt-image-2") || normalized.includes("gpt-image-1.5") || normalized.includes("gpt-image-1");
}

export function overlapRatio<T>(a: readonly T[], b: readonly T[]): number {
	if (!a.length || !b.length) return 0;
	const setB = new Set(b);
	return a.filter((item) => setB.has(item)).length / Math.min(a.length, b.length);
}

export function compareCreatedAtDesc<T extends { createdAt: number }>(a: T, b: T) {
	return b.createdAt - a.createdAt;
}

export function compareNameAsc<T extends { name: string }>(a: T, b: T) {
	return a.name.localeCompare(b.name);
}

export function formatIdentifierLabel(value?: string | null, fallback = "unknown"): string {
	return String(value ?? fallback)
		.replace(/_/g, " ")
		.toLowerCase()
		.replace(/\b\w/g, (char) => char.toUpperCase());
}

export function alphanumericToken(value: unknown): string {
	return typeof value === "string" ? value.toLowerCase().replace(/[^a-z0-9]/g, "") : "";
}

export function alphanumericWords(value: unknown, options: { maxLength?: number } = {}): string {
	const normalized = String(value ?? "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.replace(/\s+/g, " ")
		.trim();
	return options.maxLength === undefined ? normalized : normalized.slice(0, options.maxLength).trim();
}

export function trimText(value: string, max = 64): string {
	return value.length > max ? `${value.slice(0, max)}...` : value;
}

export function normalizeText(value: unknown, options: { maxLength?: number; collapseWhitespace?: boolean } = {}): string {
	if (typeof value !== "string") return "";
	const maxLength = options.maxLength ?? 1400;
	const text = options.collapseWhitespace === false ? value.trim() : value.trim().replace(/\s+/g, " ");
	return text.slice(0, maxLength);
}

export function normalizeStringArray(value: unknown, options: { maxItems?: number; maxLength?: number } = {}): string[] {
	if (!Array.isArray(value)) return [];
	const maxItems = options.maxItems ?? 8;
	const maxLength = options.maxLength ?? 240;
	return value
		.flatMap((item) => {
			const result = normalizeText(item, { maxLength });
			return result ? [result] : [];
		})
		.slice(0, maxItems);
}

export function safeFileBase(name: string, fallback = "file"): string {
	const base = name.replace(/\.[^.]+$/, "") || fallback;
	return base.replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/^-+|-+$/g, "") || fallback;
}

export function safeFileSegment(value: unknown, options: { fallback?: string; maxLength?: number; lowercase?: boolean } = {}): string {
	const fallback = options.fallback ?? "";
	const maxLength = options.maxLength ?? 80;
	const text = typeof value === "string" ? value.trim() : String(value ?? "").trim();
	const base = safeFileBase(options.lowercase === false ? text : text.toLowerCase(), fallback);
	return base.slice(0, maxLength);
}

export function safeSlugSegment(value: unknown, options: { fallback?: string; maxLength?: number } = {}): string {
	const fallback = options.fallback ?? "segment";
	const maxLength = options.maxLength ?? 64;
	const cleaned = String(value ?? "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.slice(0, maxLength);
	return cleaned || fallback;
}

export function safeFileName(name: string, fallback = "file"): string {
	const trimmed = name.trim();
	if (!trimmed) return fallback;
	const lastDot = trimmed.lastIndexOf(".");
	if (lastDot <= 0) return safeFileBase(trimmed, fallback);
	const base = safeFileBase(trimmed.slice(0, lastDot), fallback);
	const ext = trimmed.slice(lastDot).replace(/[^a-zA-Z0-9.]/g, "");
	return `${base}${ext || ""}`;
}

export function contentTypeForFilename(filename: string): string {
	const normalized = filename.toLowerCase();
	if (normalized.endsWith(".jpg") || normalized.endsWith(".jpeg")) return "image/jpeg";
	if (normalized.endsWith(".webp")) return "image/webp";
	if (normalized.endsWith(".js")) return "text/javascript";
	if (normalized.endsWith(".css")) return "text/css";
	if (normalized.endsWith(".png")) return "image/png";
	if (normalized.endsWith(".ico")) return "image/x-icon";
	if (normalized.endsWith(".svg")) return "image/svg+xml";
	if (normalized.endsWith(".json")) return "application/json";
	if (normalized.endsWith(".mp4")) return "video/mp4";
	if (normalized.endsWith(".webm")) return "video/webm";
	return "application/octet-stream";
}

export function normalizePosixPath(path: string): string {
	const absolute = path.startsWith("/");
	const parts: string[] = [];
	for (const part of path.split("/")) {
		if (!part || part === ".") continue;
		if (part === "..") {
			if (parts.length > 0 && parts[parts.length - 1] !== "..") {
				parts.pop();
			} else if (!absolute) {
				parts.push(part);
			}
			continue;
		}
		parts.push(part);
	}
	return `${absolute ? "/" : ""}${parts.join("/")}` || (absolute ? "/" : ".");
}

export function resolvePosixPath(...paths: string[]): string {
	let resolved = "";
	for (const path of paths) {
		if (!path) continue;
		resolved = path.startsWith("/") ? path : `${resolved || "."}/${path}`;
	}
	return normalizePosixPath(resolved || ".");
}

export async function streamText(stream: ReadableStream<Uint8Array> | null | undefined): Promise<string> {
	return stream ? new Response(stream).text() : "";
}

export async function streamArrayBuffer(stream: ReadableStream<Uint8Array> | null | undefined): Promise<ArrayBuffer> {
	return stream ? new Response(stream).arrayBuffer() : new ArrayBuffer(0);
}

export function bytesToText(bytes?: Uint8Array | null): string {
	return bytes ? new TextDecoder().decode(bytes) : "";
}

export function commandOutputText(result: { stdout?: Uint8Array | null; stderr?: Uint8Array | null }): string {
	return [bytesToText(result.stdout), bytesToText(result.stderr)].join("\n").trim();
}

export function parseLLMJson<T>(raw: string, fallback: T): T {
	let text = raw.trim();
	if (!text) return fallback;
	const fenceMatch = text.match(/```(?:json)?\s*\n?([\s\S]*?)```/);
	if (fenceMatch?.[1]) text = fenceMatch[1].trim();
	try {
		return JSON.parse(text) as T;
	} catch {}
	const start = text.indexOf("[");
	const end = text.lastIndexOf("]");
	if (start !== -1 && end !== -1) {
		try {
			return JSON.parse(text.slice(start, end + 1)) as T;
		} catch {}
	}
	const objectStart = text.indexOf("{");
	const objectEnd = text.lastIndexOf("}");
	if (objectStart !== -1 && objectEnd !== -1) {
		try {
			return JSON.parse(text.slice(objectStart, objectEnd + 1)) as T;
		} catch {}
	}
	return fallback;
}

export function parseJson<T>(raw: string, fallback: T): T {
	try {
		return JSON.parse(raw) as T;
	} catch {
		return fallback;
	}
}

export function parseJsonOrThrow<T = unknown>(raw: string): T {
	return JSON.parse(raw) as T;
}

export async function readRequestJson<T = Record<string, unknown>>(request: Request, fallback = {} as T): Promise<T> {
	try {
		return (await request.json()) as T;
	} catch {
		return fallback;
	}
}

export function corsHeaders(
	contentTypeOrOptions: string | { contentType?: string; maxAge?: number; allowedHeaders?: string; allowedMethods?: string } = {},
	maxAge?: number,
): Record<string, string> {
	const options = typeof contentTypeOrOptions === "string" ? { contentType: contentTypeOrOptions, maxAge } : contentTypeOrOptions;
	const headers: Record<string, string> = {
		"Access-Control-Allow-Origin": "*",
		"Access-Control-Allow-Methods": options.allowedMethods ?? "GET, POST, OPTIONS",
		"Access-Control-Allow-Headers":
			options.allowedHeaders ?? "Authorization, Content-Type, X-App-Content-Admin-Token, X-App-Instance-Id, X-Apps-Runtime-Admin-Token",
	};
	if (options.contentType) headers["Content-Type"] = options.contentType;
	if (options.maxAge) headers["Cache-Control"] = `public, max-age=${options.maxAge}`;
	return headers;
}

export function jsonResponse(body: unknown, options: number | { status?: number; headers?: HeadersInit } = 200): Response {
	const status = typeof options === "number" ? options : (options.status ?? 200);
	return new Response(JSON.stringify(body), {
		status,
		headers: (typeof options === "number" ? undefined : options.headers) ?? corsHeaders({ contentType: "application/json" }),
	});
}

export function adminTokenAuthResponse(
	request: Request,
	options: { tokenEnvName: string; headerName: string; env?: Record<string, string | undefined> },
): Response | null {
	const token = optionalEnv(options.tokenEnvName, options.env);
	if (!token) return jsonResponse({ error: "admin_auth_not_configured" }, 503);
	const authorization = request.headers.get("Authorization");
	const headerToken = request.headers.get(options.headerName);
	if (authorization === `Bearer ${token}` || headerToken === token) return null;
	return jsonResponse({ error: "unauthorized" }, 401);
}

export function parseJsonRecord<T extends JsonRecord>(raw: string, fallback: T): T {
	try {
		const parsed = JSON.parse(raw);
		return jsonRecord(parsed) ? (parsed as T) : fallback;
	} catch {
		return fallback;
	}
}

export function parseLLMJsonRecord<T extends JsonRecord>(raw: string, fallback: T): T {
	const parsed = parseLLMJson<unknown>(raw, fallback);
	return jsonRecord(parsed) ? (parsed as T) : fallback;
}

const TEMPERATURE_UNSUPPORTED_MODEL = /^(?:gpt-5(?:[.-]|$)|o\d(?:[.-]|$))/i;

export function llmTemperature(model: string, temperature: number): { temperature?: number } {
	return TEMPERATURE_UNSUPPORTED_MODEL.test(model.trim()) ? {} : { temperature };
}

export function errorMessage(error: unknown): string {
	return error instanceof Error ? error.message : String(error);
}

export function errorMessageOr(error: unknown, fallback: string): string {
	return error instanceof Error && error.message.trim() ? error.message : fallback;
}

export function errorMessageLike(error: unknown, fallback = "Unknown error"): string {
	if (error instanceof Error && error.message.trim()) return error.message;
	if (typeof error === "string" && error.trim()) return error.trim();
	if (error && typeof error === "object") {
		const record = error as Record<string, unknown>;
		for (const key of ["error", "message", "errorMessage", "reason"]) {
			const value = record[key];
			if (typeof value === "string" && value.trim()) return value.trim();
		}
		for (const key of ["stderr", "stdout"]) {
			const value = record[key];
			if (!value) continue;
			const text = typeof value === "string" ? value : typeof value === "object" && "toString" in value ? String(value) : "";
			if (text.trim()) return text.trim();
		}
		try {
			const json = JSON.stringify(error);
			if (json && json !== "{}") return json;
		} catch {}
	}
	return fallback;
}

export type JsonRecord = Record<string, unknown>;

export function jsonRecord(value: unknown): JsonRecord | null {
	return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonRecord) : null;
}

export function isJsonRecord(value: unknown): value is JsonRecord {
	return jsonRecord(value) !== null;
}

export function jsonRecordOrEmpty(value: unknown): JsonRecord {
	return jsonRecord(value) ?? {};
}

export function jsonArray(value: unknown): unknown[] {
	return Array.isArray(value) ? value : [];
}

export function jsonRecordArray(value: unknown): JsonRecord[] {
	return Array.isArray(value) ? value.filter((entry): entry is JsonRecord => jsonRecord(entry) !== null) : [];
}

export function stringArray(value: unknown, options: { omitEmpty?: boolean } = {}): string[] {
	if (!Array.isArray(value)) return [];
	return value.filter((item): item is string => typeof item === "string" && (!options.omitEmpty || item.trim().length > 0));
}

export function stringList(value: unknown, options: { splitString?: boolean } = {}): string[] {
	if (Array.isArray(value)) {
		return value.flatMap((entry) => {
			const result = String(entry).trim();
			return result ? [result] : [];
		});
	}
	if (options.splitString && typeof value === "string") {
		return value.split(/\n|,/).flatMap((entry) => {
			const result = entry.trim();
			return result ? [result] : [];
		});
	}
	return [];
}

export function splitStringList(value: unknown): string[] {
	return stringList(value, { splitString: true });
}

export function splitStringListOr(value: unknown, fallback: readonly string[]): string[] {
	const result = splitStringList(value);
	return result.length > 0 ? result : [...fallback];
}

export function stringValueList(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	return value.flatMap((entry) => {
		const result = String(entry ?? "").trim();
		return result ? [result] : [];
	});
}

export function compactStringValues(values: readonly unknown[]): string[] {
	return values.flatMap((value) => {
		const result = String(value ?? "").trim();
		return result ? [result] : [];
	});
}

export function joinCompactStrings(values: readonly unknown[], separator = "\n"): string {
	return compactStringValues(values).join(separator);
}

export function stringLeafValues(value: unknown): string[] {
	if (typeof value === "string") return [value];
	if (Array.isArray(value)) return value.flatMap(stringLeafValues);
	const record = jsonRecord(value);
	if (record) return Object.values(record).flatMap(stringLeafValues);
	return [];
}

export function uniqueStringList(value: unknown, options: { limit?: number } = {}): string[] {
	const seen = new Set<string>();
	const result: string[] = [];
	for (const entry of stringValueList(value)) {
		if (seen.has(entry)) continue;
		seen.add(entry);
		result.push(entry);
		if (options.limit !== undefined && result.length >= options.limit) break;
	}
	return result;
}

const DEFAULT_KEYWORD_STOPWORDS = new Set([
	"about",
	"after",
	"again",
	"because",
	"being",
	"could",
	"every",
	"from",
	"have",
	"just",
	"like",
	"more",
	"should",
	"that",
	"their",
	"there",
	"this",
	"what",
	"when",
	"where",
	"which",
	"with",
	"would",
]);

export function extractKeywordList(
	value: unknown,
	options: { limit?: number; minLength?: number; stopwords?: Iterable<string>; alphabeticalTiebreak?: boolean } = {},
): string[] {
	const limit = options.limit ?? 10;
	const minLength = options.minLength ?? 3;
	const stopwords = new Set([...DEFAULT_KEYWORD_STOPWORDS, ...(options.stopwords ?? [])].map((word) => word.toLowerCase()));
	const counts = new Map<string, number>();
	for (const token of displayString(value)
		.toLowerCase()
		.match(/[a-z][a-z0-9]*/g) ?? []) {
		if (token.length < minLength || stopwords.has(token)) continue;
		counts.set(token, (counts.get(token) ?? 0) + 1);
	}
	return Array.from(counts.entries())
		.sort((a, b) => b[1] - a[1] || (options.alphabeticalTiebreak ? a[0].localeCompare(b[0]) : 0))
		.slice(0, limit)
		.map(([token]) => token);
}

export function finiteNumberList(value: unknown): number[] {
	if (!Array.isArray(value)) return [];
	return value.flatMap((entry) => {
		const result = Number(entry);
		return Number.isFinite(result) ? [result] : [];
	});
}

export function jsonString(value: unknown): string | null {
	return typeof value === "string" && value.trim() ? value.trim() : null;
}

export const requiredString = jsonString;

export function firstStringValue(...values: unknown[]): string | null {
	for (const value of values) {
		const text = jsonString(value);
		if (text) return text;
	}
	return null;
}

export function hasText(value: unknown): boolean {
	return jsonString(value) !== null;
}

export function displayString(value: unknown, fallback = ""): string {
	return jsonString(value) ?? fallback;
}

export function stringValueOr(value: unknown, fallback: string): string {
	const result = String(value ?? fallback).trim();
	return result || fallback;
}

export function optionalString(value: unknown): string | undefined {
	if (value === undefined || value === null) return undefined;
	const result = String(value).trim();
	return result ? result : undefined;
}

export function normalizeBundleId(value: unknown): string {
	return String(value ?? "")
		.trim()
		.toLowerCase();
}

export function jsonNumber(value: unknown): number | null {
	return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function jsonPath(root: unknown, path: string): unknown {
	return path.split(".").reduce<unknown>((current, key) => jsonRecord(current)?.[key], root);
}

export function firstJsonPath(root: unknown, paths: readonly string[]): unknown {
	for (const path of paths) {
		const value = jsonPath(root, path);
		if (value !== undefined && value !== null && value !== "") return value;
	}
	return undefined;
}

export function jsonStringAt(root: unknown, path: string): string | null {
	return jsonString(jsonPath(root, path));
}

export function configStringOrEnv(
	config: unknown,
	configPath: string | undefined,
	envName: string,
	env: Record<string, string | undefined> | undefined = typeof process === "undefined" ? undefined : process.env,
): string | null {
	if (configPath) {
		const value = jsonStringAt(config, configPath);
		if (value) return value;
	}
	const envValue = env?.[envName]?.trim();
	return envValue || null;
}

export function firstJsonStringAt(root: unknown, paths: readonly string[]): string | null {
	return jsonString(firstJsonPath(root, paths));
}

export function jsonNumberAt(root: unknown, path: string): number | null {
	return jsonNumber(jsonPath(root, path));
}

export function firstJsonNumberAt(root: unknown, paths: readonly string[]): number | null {
	return jsonNumber(firstJsonPath(root, paths));
}

export function jsonArrayAt(root: unknown, path: string): unknown[] {
	return jsonArray(jsonPath(root, path));
}

export function firstJsonArrayAt(root: unknown, paths: readonly string[]): unknown[] {
	for (const path of paths) {
		const value = jsonArrayAt(root, path);
		if (value.length > 0) return value;
	}
	return [];
}

export function jsonRecordAt(root: unknown, path: string): JsonRecord | null {
	return jsonRecord(jsonPath(root, path));
}

export function compactObject<T extends Record<string, unknown>>(value: T, options: { omitEmptyString?: boolean; omitNull?: boolean } = {}): Partial<T> {
	return Object.fromEntries(
		Object.entries(value).filter(([, entry]) => {
			if (entry === undefined) return false;
			if (options.omitNull && entry === null) return false;
			if (options.omitEmptyString && entry === "") return false;
			return true;
		}),
	) as Partial<T>;
}

export function deepMergeRecords(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
	const result = { ...target };
	for (const key of Object.keys(source)) {
		const sourceValue = source[key];
		const targetValue = target[key];
		if (
			sourceValue &&
			typeof sourceValue === "object" &&
			!Array.isArray(sourceValue) &&
			targetValue &&
			typeof targetValue === "object" &&
			!Array.isArray(targetValue)
		) {
			result[key] = deepMergeRecords(targetValue as Record<string, unknown>, sourceValue as Record<string, unknown>);
		} else {
			result[key] = sourceValue;
		}
	}
	return result;
}

export function normalizeUsdPrice(price?: string): string | null {
	const raw = String(price ?? "").trim();
	const normalized = raw.includes(",") && !raw.includes(".") && /,\d{1,2}\b/.test(raw) ? raw.replace(",", ".") : raw.replace(/,/g, "");
	const match = normalized.match(/\d+(\.\d+)?/);
	return match?.[0] ? Number.parseFloat(match[0]).toFixed(2) : null;
}

export function basenameFromPath(value?: string | null): string {
	return (value ?? "").replace(/\\/g, "/").split("/").filter(Boolean).pop() ?? "";
}

export function basenameOrPath(value: string): string {
	return basenameFromPath(value) || value;
}

export const folderNameFromPath = basenameFromPath;

const REPO_PATH_MARKERS = ["apps/", "data/", "templates/"] as const;

function looksAbsolutePath(path: string): boolean {
	return path.startsWith("/") || /^[A-Za-z]:[\\/]/.test(path);
}

function isPathWithinRoot(child: string, root: string): boolean {
	const normalizedChild = normalizePosixPath(child.replace(/\\/g, "/"));
	const normalizedRoot = normalizePosixPath(root.replace(/\\/g, "/")).replace(/\/$/, "");
	return normalizedChild === normalizedRoot || normalizedChild.startsWith(`${normalizedRoot}/`);
}

/** Strip machine-specific prefixes so paths work across developer machines. */
export function toRepoRelativePath(path: string | null | undefined, repoRoot: string = PROJECT_ROOT): string {
	const trimmed = (path ?? "").trim();
	if (!trimmed) return "";
	const normalized = trimmed.replace(/\\/g, "/");
	if (!looksAbsolutePath(normalized)) return normalizePosixPath(normalized);

	const resolvedRoot = normalizePosixPath(repoRoot.replace(/\\/g, "/")).replace(/\/$/, "");
	const resolvedPath = normalizePosixPath(normalized);
	if (isPathWithinRoot(resolvedPath, resolvedRoot)) {
		const relative = resolvedPath.slice(resolvedRoot.length + 1);
		return relative || basenameFromPath(resolvedPath);
	}

	for (const marker of REPO_PATH_MARKERS) {
		const idx = resolvedPath.indexOf(marker);
		if (idx >= 0) return resolvedPath.slice(idx);
	}

	const folder = basenameFromPath(resolvedPath);
	return folder ? `apps/${folder}` : resolvedPath;
}

/** Canonical repo-relative path for shared Convex storage and cross-machine prompts. */
export function normalizeStoredAppPath(path: string | null | undefined): string | undefined {
	const normalized = toRepoRelativePath(path, ".");
	return normalized || undefined;
}

export function imagePreviewUrl(path: string | undefined | null, width = 320) {
	if (!path) return "";
	if (/^https:\/\/[^/]+\.convex\.cloud\/api\/storage\//.test(path)) {
		return `/api/media/thumb?url=${encodeURIComponent(path)}&w=${width}&fmt=webp`;
	}
	if (path.startsWith("/api/campaigns/file/")) {
		return `${path}${path.includes("?") ? "&" : "?"}w=${width}&fmt=webp`;
	}
	return path;
}

export function hashSeed(value: string): number {
	let hash = 2166136261;
	for (let i = 0; i < value.length; i += 1) {
		hash ^= value.charCodeAt(i);
		hash = Math.imul(hash, 16777619);
	}
	return hash >>> 0 || 1;
}

export function createSeededRandom(seed: number): () => number {
	let state = seed;
	return () => {
		state = (state * 16807) % 2147483647;
		return (state - 1) / 2147483646;
	};
}

export function isPngFile(fileName: string): boolean {
	return fileExtension(fileName).toLowerCase() === ".png";
}
